import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { OnPremNetworkOutput, OnPremOutput, OnPremStorageOutput } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Box, ColumnLayout, Container, Header, SpaceBetween, Spinner, Tabs, TabsProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../../common/Constants';
import { constants } from '../../../../common/Localization';
import { formatCurrencyNoDecimals } from '../../../../common/LocalizationHelper';
import { MatchParam } from '../../../../common/types';
import { HelpPanelContext } from '../../../../contexts/HelpPanelContext';
import { TcoContext } from '../../../../contexts/TcoContext';
import { buildStorageAndNetworkSummary, generateCashFlowTable } from '../../common/Common';
import {
    buildDatabaseStorageTable,
    buildNetworkSummaryTable,
    buildServerStorageTable,
    buildStorageBackupSummaryTable,
    buildStorageOverheadSummaryTable,
    buildStorageSummaryTable,
    generateNetworkCashFlowItems,
    generateStorageCashFlowItems,
} from './OnpremStorageNetworkCostHelper';

type Props = FlashContextInterface;

const tcoService = new TcoService();
function OnpremStorageNetworkCost({ flash }: Props): JSX.Element {
    const [ loading, setLoading ] = useState(false);
    const { assessment, tcoLoading } = useContext(TcoContext);
    const [ onPremiseOutput, setOnPremiseOutput ] = useState<OnPremOutput | null>(null);
    const [ activeTabId, setActiveTabId ] = useState('serverStorage');
    const { panelHandler } = useContext(HelpPanelContext);

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const isMounted = useIsMounted();
    const loadOnPremiseData = async () => {
        setLoading(true);
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const onPremAssessment = await tcoService.getOnPremise(tcoAssessmentId);
                if (isMounted()) {
                    setOnPremiseOutput(onPremAssessment.output.onPremises);
                }
            }
        } catch(err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_ONPREM);
            }
        } finally {
            if (isMounted()) { 
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.ONPREM_STORAGE_AND_NETWORK);
    }, [ panelHandler ]);

    useEffect(() => {
        if (assessment) {
            loadOnPremiseData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    const buildStorageNetworkTabs = (storage: OnPremStorageOutput, network: OnPremNetworkOutput, currency: string): TabsProps.Tab[] => {
        const tabs: TabsProps.Tab[] = [{
            id: 'serverStorage',
            label: constants.STORAGE_SERVER,
            content: buildServerStorageTable(storage.volumePerServer, currency),
        }, {
            id: 'databaseStorage',
            label: constants.STORAGE_DATABASE,
            content: buildDatabaseStorageTable(storage.volumePerDatabase, currency),
        }, {
            id: 'storageCashFlow',
            label: constants.ON_PREM_STORAGE_CASH_FLOW,
            content: generateCashFlowTable(generateStorageCashFlowItems(storage.annualCosts, storage.total), storage.annualCosts.length, currency),
        }, {
            id: 'networkCashFlow',
            label: constants.ON_PREM_NETWORK_CASH_FLOW,
            content: generateCashFlowTable(generateNetworkCashFlowItems(network), network.cashFlow.yearly.total.length, currency),
        }];
        return tabs;
    };

    const buildOnPremStorageContent = (): JSX.Element => {
        if (tcoLoading || loading || !onPremiseOutput) {
            return (
                <Spinner size='big' />
            );
        }
       
        const storage = onPremiseOutput.storage;
        const network = onPremiseOutput.network;
        const storageAndNetwork = onPremiseOutput.storageAndNetwork;
        const currency = onPremiseOutput.currency;

        return (
            <SpaceBetween size='l'>
                <Header variant='h1'>{constants.ON_PREM_STORAGE_COST_TITLE}</Header>
                {
                    !storage.backupSummary.serverStorageBackup &&
                    <Alert type='warning' dismissible={false}>{constants.NO_BACKUP_WARNING}</Alert>
                }
                {buildStorageSummaryTable(storage, currency)}
                <Header variant='h2'>{constants.ON_PREM_STORAGE_BACK_UP_TITLE}</Header>
                {buildStorageBackupSummaryTable(storage, currency)}
                <Header variant='h2'>{constants.ON_PREM_OVERHEAD}</Header>
                {buildStorageOverheadSummaryTable(storage, currency)}
                <ColumnLayout disableGutters columns={2} variant='default' className='storage-network-total-grid'>
                    <Box variant='h3' float='left'>{constants.ON_PREM_STORAGE_TOTAL_COST}</Box>
                    <Box variant='h3' float='right'>{formatCurrencyNoDecimals(storage.totalCost, currency)}</Box>
                </ColumnLayout>
                <Header variant='h1'>{constants.ON_PREM_NETWORK_COST}</Header>
                {buildNetworkSummaryTable(network, currency)}
                <ColumnLayout disableGutters columns={2} variant='default' className='storage-network-total-grid'>
                    <Box variant='h3' float='left'>{constants.ON_PREM_NETWORK_TOTAL_COST}</Box>
                    <Box variant='h3' float='right'>{formatCurrencyNoDecimals(network.totalCost, currency)}</Box>
                </ColumnLayout>
                <Header variant='h1'>{constants.TOTAL}</Header>
                {buildStorageAndNetworkSummary(
                    storageAndNetwork.totalCost,
                    storage.manualAdjustment.value,
                    network.manualAdjustment.value,
                    storageAndNetwork.adjustedTotalCost,
                    currency,
                    storage.manualAdjustment.desc,
                    network.manualAdjustment.desc,
                )}
                <Header variant='h2'>{constants.DETAILS}</Header>
                <Tabs
                    tabs={buildStorageNetworkTabs(storage, network, currency)}
                    activeTabId={activeTabId}
                    onChange={({ detail }) => setActiveTabId(detail.activeTabId)}></Tabs>
            </SpaceBetween>
        );
    };
    return (
        <Container data-testid='container'>
            {buildOnPremStorageContent()}
        </Container>
    );
};

export default withFlashContext(OnpremStorageNetworkCost);
